import React, {createRef, useEffect} from 'react';
import {Redirect, Route, useLocation} from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';

import {useSegment} from 'hooks/segment';
import ProtectedRoute from 'common/auth/ProtectedRoute';
import useGenerateRoute from 'hooks/use-generate-route/useGenerateRoute';

const previousLocationRef = createRef(null);

/**
 * Component that renders a route based on the route configuration.
 * @param {Object} route
 * @return {React.JSX.Element}
 */
const RouteRenderer = (route) => {
    const {trackPage} = useSegment();
    const location = useLocation();
    const {params} = route.computedMatch;
    const {generateRouteWithParams} = useGenerateRoute();

    /**
     * Track the page view when the route is rendered
     */
    useEffect(
        function trackPageEffect() {
            if (route.name && !route.trackingDisabled) {
                trackPage({
                    name: `${route.name} - Landed`,
                    properties: {
                        'Facility ID': params.facilityId || null,
                        page: params.tabId || null,
                        'From Screen': previousLocationRef.current,
                        referrer: previousLocationRef.current,
                        ...params,
                    },
                });
            }

            return () => {
                previousLocationRef.current = location.pathname;
            };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [route.name, location.pathname]
    );

    if (route.redirect) {
        const redirectPath = isEmpty(params)
            ? route.redirect
            : generateRouteWithParams(route.redirect, params);

        return (
            <Route path={route.path}>
                <Redirect to={redirectPath} />
            </Route>
        );
    }

    if (route.protected) {
        return (
            <ProtectedRoute
                exact={route.exact}
                path={route.path}
                location={route.location}
                render={(props) => {
                    return (
                        <route.component
                            {...props}
                            previousLocationRef={previousLocationRef}
                        />
                    );
                }}
            />
        );
    }

    return (
        <Route
            exact={route.exact}
            path={route.path}
            render={(props) => {
                return (
                    <route.component
                        {...props}
                        previousLocationRef={previousLocationRef}
                    />
                );
            }}
        />
    );
};

export default RouteRenderer;
